// Hand-written SDK for the Curlec Route payout mutations (PaymentTransferMutations.Custom.cs /
// PaymentTransferQueries.Custom.cs on the API). Admin-only — the API checks the persona claim.
// Same envelope shape as checkout.sdk.ts.
import { Injectable } from '@angular/core';
import { GraphqlApiService } from '../../api/graphql-api.service';
import type { CheckoutEnvelope } from '../checkout/checkout.sdk';

export type RoutePayoutMode = 'dryrun' | 'live' | '';

export interface RouteTransferResult {
  transferId: number;
  transferRef: string;
  /** created | pending | processed | failed … */
  status: string;
  settlementStatus?: string | null;
  simulated: boolean;
  amount: number;
  onHold: boolean;
  onHoldUntil?: string | null;
  /** The ledger row is now settled (paid). */
  ledgerSettled: boolean;
}

/** A vendor's Curlec Route linked account (VendorPayoutAccount). */
export interface VendorPayoutAccountView {
  id: number;
  linkedAccountRef: string;
  /** pending | activated | needs_clarification | suspended | rejected */
  status: string;
  activatedDate?: string | null;
}

/** The vendor's Curlec Route linked account as Admin › Vendors › Verification shows it. */
export interface LinkedAccountView {
  id: number;
  vendorId: number;
  linkedAccountRef: string;
  /** pending | activated | needs_clarification | suspended | rejected */
  status: string;
  /** Last setup step completed: account | stakeholder | product | settlements | done. null = linked by hand. */
  setupStep: string | null;
  accountEmail: string | null;
  productRef: string | null;
  /** What Curlec still needs ("field — description"). */
  requirements: string[];
  failureReason: string | null;
  bankName: string | null;
  accountNumberMasked: string | null;
  beneficiaryName: string | null;
  activatedDate: string | null;
  lastSyncedDate: string | null;
  createdDate: string | null;
  createdByName: string | null;
}

/** What the admin submits to create the account (server re-validates every field). */
export interface CreateLinkedAccountInput {
  vendorId: number;
  legalBusinessName: string;
  customerFacingName?: string | null;
  businessType: string;
  category: string;
  subcategory: string;
  contactName: string;
  phone: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  bankId: number;
  accountNumber: string;
  beneficiaryName: string;
  tncAccepted: boolean;
  replace: boolean;
}

const LINKED_ACCOUNT_FIELDS = `id vendorId linkedAccountRef status setupStep accountEmail productRef requirements failureReason
  bankName accountNumberMasked beneficiaryName activatedDate lastSyncedDate createdDate createdByName`;

export interface CurlecRefundResult {
  amount: number;
  /** Curlec refund ids (rfnd_…), one per customer payment refunded. */
  refundRefs: string[];
  simulated: boolean;
  ledgerSettled: boolean;
}

const RESULT_FIELDS = `transferId transferRef status settlementStatus simulated amount onHold onHoldUntil ledgerSettled`;

@Injectable({ providedIn: 'root' })
export class RouteSdk {
  constructor(private api: GraphqlApiService) {}

  /** 'dryrun' (recorded, no money moves) or 'live'. Empty for non-admins. */
  async payoutMode(): Promise<RoutePayoutMode> {
    const r = await this.api.request<{ routePayoutMode: string }>(`query { routePayoutMode }`, {});
    return (r.routePayoutMode ?? '') as RoutePayoutMode;
  }

  /** "Pay via Curlec" on a pending early-release advance (fund_front) or completion payout (release) row. */
  async payLedgerEntry(ledgerEntryId: number): Promise<CheckoutEnvelope<RouteTransferResult>> {
    const q = `mutation PayLedgerEntryViaRoute($ledgerEntryId:Long!){
      payLedgerEntryViaRoute(ledgerEntryId:$ledgerEntryId){ success error message data { ${RESULT_FIELDS} } }
    }`;
    const r = await this.api.request<{ payLedgerEntryViaRoute: CheckoutEnvelope<RouteTransferResult> }>(q, { ledgerEntryId });
    return r.payLedgerEntryViaRoute;
  }

  /** Completion payouts: onHold=false releases now; onHold=true + holdUntil ('yyyy-MM-dd') holds until then, or until released. */
  async modifyHold(transferId: number, onHold: boolean, holdUntil?: string | null): Promise<CheckoutEnvelope<RouteTransferResult>> {
    const q = `mutation ModifyRouteTransferHold($transferId:Long!,$onHold:Boolean!,$holdUntil:String){
      modifyRouteTransferHold(transferId:$transferId,onHold:$onHold,holdUntil:$holdUntil){ success error message data { ${RESULT_FIELDS} } }
    }`;
    const r = await this.api.request<{ modifyRouteTransferHold: CheckoutEnvelope<RouteTransferResult> }>(q, {
      transferId, onHold, holdUntil: holdUntil || null,
    });
    return r.modifyRouteTransferHold;
  }

  /** Refund a pending customer refund (ledger `refund` row) to the customer's Curlec payment(s). */
  async refundLedgerEntry(ledgerEntryId: number): Promise<CheckoutEnvelope<CurlecRefundResult>> {
    const q = `mutation RefundLedgerEntryViaCurlec($ledgerEntryId:Long!){
      refundLedgerEntryViaCurlec(ledgerEntryId:$ledgerEntryId){ success error message data { amount refundRefs simulated ledgerSettled } }
    }`;
    const r = await this.api.request<{ refundLedgerEntryViaCurlec: CheckoutEnvelope<CurlecRefundResult> }>(q, { ledgerEntryId });
    return r.refundLedgerEntryViaCurlec;
  }

  /** Pull a clawed-back early-release advance back from the vendor (cancelled booking). amount null = all owed. */
  async recoverAdvance(transferId: number, amount?: number | null): Promise<CheckoutEnvelope<RouteTransferResult>> {
    const q = `mutation RecoverAdvanceViaCurlec($transferId:Long!,$amount:Decimal){
      recoverAdvanceViaCurlec(transferId:$transferId,amount:$amount){ success error message data { ${RESULT_FIELDS} } }
    }`;
    const r = await this.api.request<{ recoverAdvanceViaCurlec: CheckoutEnvelope<RouteTransferResult> }>(q, {
      transferId, amount: amount ?? null,
    });
    return r.recoverAdvanceViaCurlec;
  }

  /** Split an already-captured Curlec payment into held vendor transfers (Route › Payments). */
  async splitPayment(paymentId: number): Promise<CheckoutEnvelope<unknown>> {
    const q = `mutation SplitPaymentToVendors($paymentId:Long!){
      splitPaymentToVendors(paymentId:$paymentId){ success error message data { paymentId simulated lines { vendorId vendorName share feePct amount transferRef note } } }
    }`;
    const r = await this.api.request<{ splitPaymentToVendors: CheckoutEnvelope<unknown> }>(q, { paymentId });
    return r.splitPaymentToVendors;
  }

  /**
   * Phase 3: bring the vendors' HELD split shares on a booking back in line with the order — called
   * after a removal, swap or order change. Server no-op for bookings never split on payment.
   */
  async rebalanceHeldShares(bookingId: number): Promise<CheckoutEnvelope<unknown>> {
    const q = `mutation RebalanceHeldShares($bookingId:Long!){
      rebalanceHeldShares(bookingId:$bookingId){ success error message data { bookingId simulated escrow lines { vendorId vendorName held target reversed added refs note } } }
    }`;
    const r = await this.api.request<{ rebalanceHeldShares: CheckoutEnvelope<unknown> }>(q, { bookingId });
    return r.rebalanceHeldShares;
  }

  /** The vendor's linked account, or null when none is linked yet. */
  async vendorPayoutAccount(vendorId: number): Promise<VendorPayoutAccountView | null> {
    const q = `query ListVendorPayoutAccount($input: DynamicQueryJsonInput) {
      listVendorPayoutAccount(input: $input) { success error data { id linkedAccountRef status activatedDate isDeleted } }
    }`;
    const queryJson = JSON.stringify({
      baseTable: 'VendorPayoutAccount', baseAlias: 'vendorPayoutAccount',
      selects: ['id', 'linkedAccountRef', 'status', 'activatedDate', 'isDeleted'].map(f => ({ columnName: f, alias: f, isTrusted: true })),
      joins: [],
      where: [{ column: 'vendorId', operator: '=', value: vendorId, logic: 'And', subFilters: [], isTrusted: false }],
      having: [], sorts: [{ column: 'id', desc: true, isTrusted: true }], groupBy: [],
      skip: 0, take: 5, options: { strictMode: false, useCache: false },
    });
    const r = await this.api.request<{ listVendorPayoutAccount: { success: boolean; data?: Array<VendorPayoutAccountView & { isDeleted?: boolean }> } }>(
      q, { input: { queryJson } });
    const row = (r.listVendorPayoutAccount?.data ?? []).find(a => !a.isDeleted);
    return row ? { id: row.id, linkedAccountRef: row.linkedAccountRef ?? '', status: row.status ?? '', activatedDate: row.activatedDate ?? null } : null;
  }

  /** Attach the vendor's Curlec linked account id (acc_…). */
  /** The vendor's Curlec account (data null when none). Admin only. */
  async vendorLinkedAccount(vendorId: number): Promise<CheckoutEnvelope<LinkedAccountView>> {
    const q = `query VendorLinkedAccount($vendorId:Long!){
      vendorLinkedAccount(vendorId:$vendorId){ success error message data { ${LINKED_ACCOUNT_FIELDS} } }
    }`;
    const r = await this.api.request<{ vendorLinkedAccount: CheckoutEnvelope<LinkedAccountView> }>(q, { vendorId });
    return r.vendorLinkedAccount;
  }

  /** "Create Curlec account" — runs Curlec's four account steps (resumable: Retry continues from the failed step). */
  async createVendorLinkedAccount(input: CreateLinkedAccountInput): Promise<CheckoutEnvelope<LinkedAccountView>> {
    const q = `mutation CreateVendorLinkedAccount($input: CreateLinkedAccountInput!){
      createVendorLinkedAccount(input:$input){ success error message data { ${LINKED_ACCOUNT_FIELDS} } }
    }`;
    const r = await this.api.request<{ createVendorLinkedAccount: CheckoutEnvelope<LinkedAccountView> }>(q, { input });
    return r.createVendorLinkedAccount;
  }

  /** Re-read the account's Route activation status from Curlec. */
  async refreshVendorLinkedAccount(vendorId: number): Promise<CheckoutEnvelope<LinkedAccountView>> {
    const q = `mutation RefreshVendorLinkedAccount($vendorId:Long!){
      refreshVendorLinkedAccount(vendorId:$vendorId){ success error message data { ${LINKED_ACCOUNT_FIELDS} } }
    }`;
    const r = await this.api.request<{ refreshVendorLinkedAccount: CheckoutEnvelope<LinkedAccountView> }>(q, { vendorId });
    return r.refreshVendorLinkedAccount;
  }

  /** Re-send the settlement bank account (when Curlec asks for clarification). */
  async updateVendorLinkedAccountBank(vendorId: number, bankId: number, accountNumber: string, beneficiaryName: string): Promise<CheckoutEnvelope<LinkedAccountView>> {
    const q = `mutation UpdateVendorLinkedAccountBank($vendorId:Long!,$bankId:Long!,$accountNumber:String!,$beneficiaryName:String!){
      updateVendorLinkedAccountBank(vendorId:$vendorId,bankId:$bankId,accountNumber:$accountNumber,beneficiaryName:$beneficiaryName){ success error message data { ${LINKED_ACCOUNT_FIELDS} } }
    }`;
    const r = await this.api.request<{ updateVendorLinkedAccountBank: CheckoutEnvelope<LinkedAccountView> }>(q, { vendorId, bankId, accountNumber, beneficiaryName });
    return r.updateVendorLinkedAccountBank;
  }

  async linkVendorPayoutAccount(vendorId: number, linkedAccountRef: string): Promise<CheckoutEnvelope<unknown>> {
    const q = `mutation LinkVendorPayoutAccount($vendorId:Long!,$linkedAccountRef:String!){
      linkVendorPayoutAccount(vendorId:$vendorId,linkedAccountRef:$linkedAccountRef){ success error message }
    }`;
    const r = await this.api.request<{ linkVendorPayoutAccount: CheckoutEnvelope<unknown> }>(q, { vendorId, linkedAccountRef });
    return r.linkVendorPayoutAccount;
  }
}
